(function () {
  'use strict';

  function init() {
    const gallery = document.querySelector('media-gallery');
    if (!gallery) return;

    const slider = gallery.querySelector('[id^="Slider-Gallery-"]');
    if (!slider) return;

    new ProductGallery(gallery, slider);
  }

  class ProductGallery {
    constructor(gallery, slider) {
      this.gallery = gallery;
      this.slider = slider;
      this.slides = [];
      this.currentIndex = 0;
      this.isOpen = false;
      this.savedScrollY = 0;

      // Active transform on img-wrap.
      // CSS uses transform-origin: 0 0, so:
      //   element point (x,y) → viewport (nl + tx + x*scale, nt + ty + y*scale)
      // where (nl, nt) is the element's natural (untransformed) position in the stage.
      this.transform = { tx: 0, ty: 0, scale: 1 };

      // Touch tracking — no scale stored here, transform is the source of truth
      this.touch = {
        isDragging: false,
        isZooming: false,
        startX: 0, startY: 0,
        lastX: 0, lastY: 0,
        prevDist: 0,
        prevMidX: 0, prevMidY: 0,
      };

      // Double-tap detection
      this.lastTapTime = 0;
      this.lastTapX = 0;
      this.lastTapY = 0;

      this.buildLightbox();
      this.bindGalleryTaps();
      this.onKeydown = this.onKeydown.bind(this);
      document.addEventListener('keydown', this.onKeydown);
    }

    // ── Slide collection ────────────────────────────────────────

    collectSlides() {
      this.slides = [];
      const items = this.slider.querySelectorAll('li[data-media-id]');
      items.forEach((item) => {
        // Skip non-image media (video/model) — Dawn handles those natively
        if (item.classList.contains('product__media-item--full')) return;

        const img = item.querySelector('.product__media img');
        if (!img) return;

        this.slides.push({
          mediaId: item.dataset.mediaId,
          imageUrl: this.getBestImageUrl(img),
          alt: img.alt || '',
        });
      });
    }

    getBestImageUrl(img) {
      const srcset = img.srcset;
      if (!srcset) return img.src;

      let bestUrl = img.src;
      let bestWidth = 0;

      srcset.split(',').forEach((entry) => {
        const parts = entry.trim().split(/\s+/);
        if (parts.length < 2) return;
        const w = parseInt(parts[1], 10);
        if (w > bestWidth) {
          bestWidth = w;
          bestUrl = parts[0];
        }
      });

      return bestUrl;
    }

    // ── Gallery tap interception ────────────────────────────────
    //
    // Two complementary paths:
    //   touchend (capture, non-passive) — PRIMARY on mobile.
    //     Tap-vs-swipe is detected via touch delta.
    //     e.preventDefault() suppresses the synthetic click so
    //     Dawn's ModalOpener never fires its own lightbox.
    //
    //   click (capture) — PRIMARY on desktop.
    //     Suppressed on touch by the touchend preventDefault above.

    bindGalleryTaps() {
      let tapStartX = 0;
      let tapStartY = 0;

      this.gallery.addEventListener(
        'touchstart',
        (e) => {
          if (e.touches.length !== 1) return;
          tapStartX = e.touches[0].clientX;
          tapStartY = e.touches[0].clientY;
        },
        { passive: true }
      );

      this.gallery.addEventListener(
        'touchend',
        (e) => {
          if (e.changedTouches.length !== 1) return;

          const dx = Math.abs(e.changedTouches[0].clientX - tapStartX);
          const dy = Math.abs(e.changedTouches[0].clientY - tapStartY);
          if (dx > 10 || dy > 10) return; // swipe — ignore

          const tapX = e.changedTouches[0].clientX;
          const tapY = e.changedTouches[0].clientY;
          const hitTarget = document.elementFromPoint(tapX, tapY) || e.target;

          const opener = hitTarget.closest('.product__modal-opener--image');
          if (!opener) return;

          const item = opener.closest('li[data-media-id]');
          if (!item) return;

          e.preventDefault(); // suppress synthetic click → blocks Dawn's ModalOpener

          this.collectSlides();
          const idx = this.slides.findIndex((s) => s.mediaId === item.dataset.mediaId);
          this.open(idx >= 0 ? idx : 0);
        },
        { capture: true }
      );

      this.gallery.addEventListener(
        'click',
        (e) => {
          const opener = e.target.closest('.product__modal-opener--image');
          if (!opener) return;

          const item = opener.closest('li[data-media-id]');
          if (!item) return;

          e.preventDefault();
          e.stopImmediatePropagation();

          this.collectSlides();
          const idx = this.slides.findIndex((s) => s.mediaId === item.dataset.mediaId);
          this.open(idx >= 0 ? idx : 0);
        },
        { capture: true }
      );
    }

    // ── Lightbox DOM ────────────────────────────────────────────

    buildLightbox() {
      const lb = document.createElement('div');
      lb.id = 'pg-lightbox';
      lb.className = 'pg-lightbox';
      lb.setAttribute('role', 'dialog');
      lb.setAttribute('aria-modal', 'true');
      lb.setAttribute('aria-label', 'Product image gallery');
      lb.hidden = true;

      lb.innerHTML = `
        <div class="pg-lightbox__ui">
          <header class="pg-lightbox__header">
            <button class="pg-lightbox__back" type="button" aria-label="Close gallery">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <polyline points="15 18 9 12 15 6"/>
              </svg>
              <span class="pg-lightbox__back-label">Close</span>
            </button>
            <span class="pg-lightbox__counter" aria-live="polite" aria-atomic="true">
              <span class="pg-lightbox__idx">1</span><span aria-hidden="true"> / </span><span class="pg-lightbox__len">1</span>
            </span>
          </header>
          <div class="pg-lightbox__stage">
            <div class="pg-lightbox__slide-wrap">
              <div class="pg-lightbox__img-wrap">
                <img class="pg-lightbox__img" src="" alt="" loading="eager" draggable="false">
              </div>
            </div>
            <button class="pg-lightbox__prev" type="button" aria-label="Previous image">
              <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <polyline points="15 18 9 12 15 6"/>
              </svg>
            </button>
            <button class="pg-lightbox__next" type="button" aria-label="Next image">
              <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <polyline points="9 18 15 12 9 6"/>
              </svg>
            </button>
          </div>
          <div class="pg-lightbox__dots" role="tablist" aria-label="Slide navigation"></div>
        </div>
      `;

      document.body.appendChild(lb);
      this.lb = lb;

      this.el = {
        stage:   lb.querySelector('.pg-lightbox__stage'),
        imgWrap: lb.querySelector('.pg-lightbox__img-wrap'),
        img:     lb.querySelector('.pg-lightbox__img'),
        idx:     lb.querySelector('.pg-lightbox__idx'),
        len:     lb.querySelector('.pg-lightbox__len'),
        back:    lb.querySelector('.pg-lightbox__back'),
        prev:    lb.querySelector('.pg-lightbox__prev'),
        next:    lb.querySelector('.pg-lightbox__next'),
        dots:    lb.querySelector('.pg-lightbox__dots'),
      };

      this.el.back.addEventListener('click', () => this.close());
      this.el.prev.addEventListener('click', () => this.goTo(this.currentIndex - 1));
      this.el.next.addEventListener('click', () => this.goTo(this.currentIndex + 1));

      this.el.stage.addEventListener('touchstart', this.onTouchStart.bind(this), { passive: true });
      this.el.stage.addEventListener('touchmove',  this.onTouchMove.bind(this),  { passive: false });
      this.el.stage.addEventListener('touchend',   this.onTouchEnd.bind(this),   { passive: true });
    }

    // ── Open / close ────────────────────────────────────────────

    open(index) {
      this.isOpen = true;
      this.currentIndex = index;
      this.resetTransform();
      this.lastTapTime = 0;

      // Save scroll position before locking.
      // position:fixed is used (not overflow:hidden) because iOS Safari
      // ignores overflow:hidden on body and allows the page to bounce-scroll.
      this.savedScrollY = window.scrollY;
      document.documentElement.classList.add('pg-lightbox-active');
      document.body.style.position = 'fixed';
      document.body.style.top = `-${this.savedScrollY}px`;
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';

      this.lb.hidden = false;
      requestAnimationFrame(() => this.lb.classList.add('pg-lightbox--open'));

      this.renderSlide(index);
      this.buildDots();
      this.el.back.focus();
    }

    close() {
      this.isOpen = false;
      this.resetTransform();
      this.lb.classList.remove('pg-lightbox--open');

      // Restore scroll lock state and exact position.
      document.documentElement.classList.remove('pg-lightbox-active');
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.overflow = '';
      window.scrollTo({ top: this.savedScrollY, behavior: 'instant' });

      setTimeout(() => { this.lb.hidden = true; }, 240);
    }

    // ── Navigation ──────────────────────────────────────────────

    goTo(index) {
      if (!this.slides.length) return;
      const next = ((index % this.slides.length) + this.slides.length) % this.slides.length;
      this.currentIndex = next;
      this.resetTransform();
      this.renderSlide(next);
      this.updateDots(next);
    }

    renderSlide(index) {
      const slide = this.slides[index];
      if (!slide) return;

      this.el.img.src = slide.imageUrl;
      this.el.img.alt = slide.alt;
      this.el.idx.textContent = index + 1;
      this.el.len.textContent = this.slides.length;

      const single = this.slides.length <= 1;
      this.el.prev.hidden = single;
      this.el.next.hidden = single;
      this.el.dots.hidden = single;
    }

    buildDots() {
      this.el.dots.innerHTML = '';
      if (this.slides.length <= 1) return;

      this.slides.forEach((_, i) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'pg-lightbox__dot';
        dot.setAttribute('role', 'tab');
        dot.setAttribute('aria-label', `Image ${i + 1} of ${this.slides.length}`);
        dot.addEventListener('click', () => this.goTo(i));
        this.el.dots.appendChild(dot);
      });

      this.updateDots(this.currentIndex);
    }

    updateDots(index) {
      this.el.dots.querySelectorAll('.pg-lightbox__dot').forEach((dot, i) => {
        const active = i === index;
        dot.classList.toggle('pg-lightbox__dot--active', active);
        dot.setAttribute('aria-selected', String(active));
      });
    }

    // ── Keyboard ────────────────────────────────────────────────

    onKeydown(e) {
      if (!this.isOpen) return;
      if (e.key === 'ArrowLeft')       this.goTo(this.currentIndex - 1);
      else if (e.key === 'ArrowRight') this.goTo(this.currentIndex + 1);
      else if (e.key === 'Escape')     this.close();
    }

    // ── Transform management ────────────────────────────────────
    //
    // CSS on img-wrap: transform-origin: 0 0
    // Applied transform: translate(tx px, ty px) scale(scale)
    //
    // With transform-origin: 0 0, element local point (x, y) maps to:
    //   viewport_x = stageRect.left + nl + tx + x * scale
    //   viewport_y = stageRect.top  + nt + ty + y * scale
    // where (nl, nt) = imgWrap.offsetLeft/offsetTop (layout position, unaffected by CSS transform)
    //
    // Zoom-to-point formula (keep anchor viewport position fixed when scaling):
    //   dxFromLeft = anchorViewportX - imgRect.left  (visual left of img-wrap)
    //   newTx = dxFromLeft * (1 - newScale/currentScale) + currentTx

    applyTransform() {
      const { tx, ty, scale } = this.transform;
      this.el.imgWrap.style.transform =
        scale === 1 && tx === 0 && ty === 0
          ? ''
          : `translate(${tx}px,${ty}px) scale(${scale})`;
    }

    resetTransform() {
      this.el.imgWrap.style.transition = '';
      this.transform = { tx: 0, ty: 0, scale: 1 };
      this.applyTransform();
    }

    // Clamp (tx, ty) so the image never fully leaves the stage.
    // When the scaled image is wider/taller than the stage, allow panning
    // only within the image's bounds (no blank gutters).
    // When smaller, force the image to stay centered on that axis.
    clampTransform(tx, ty, scale) {
      const stageW = this.el.stage.offsetWidth;
      const stageH = this.el.stage.offsetHeight;
      const imgW   = this.el.imgWrap.offsetWidth;
      const imgH   = this.el.imgWrap.offsetHeight;
      const scaledW = imgW * scale;
      const scaledH = imgH * scale;
      const nl = this.el.imgWrap.offsetLeft;
      const nt = this.el.imgWrap.offsetTop;

      let txMin, txMax, tyMin, tyMax;

      if (scaledW >= stageW) {
        // Image fills or exceeds stage width: allow panning edge-to-edge
        txMin = stageW - nl - scaledW;
        txMax = -nl;
      } else {
        // Image narrower than stage: center it, no horizontal pan
        const cx = (stageW - scaledW) / 2 - nl;
        txMin = txMax = cx;
      }

      if (scaledH >= stageH) {
        tyMin = stageH - nt - scaledH;
        tyMax = -nt;
      } else {
        const cy = (stageH - scaledH) / 2 - nt;
        tyMin = tyMax = cy;
      }

      return {
        tx: Math.max(txMin, Math.min(txMax, tx)),
        ty: Math.max(tyMin, Math.min(tyMax, ty)),
      };
    }

    // ── Lightbox touch: swipe / pan / pinch-to-zoom ──────────────

    onTouchStart(e) {
      if (e.touches.length === 2) {
        this.touch.isZooming = true;
        this.touch.isDragging = false;
        const dist = this.getTouchDist(e.touches);
        const midX = (e.touches[0].clientX + e.touches[1].clientX) / 2;
        const midY = (e.touches[0].clientY + e.touches[1].clientY) / 2;
        this.touch.prevDist = dist;
        this.touch.prevMidX = midX;
        this.touch.prevMidY = midY;
      } else if (e.touches.length === 1 && !this.touch.isZooming) {
        this.touch.isDragging = true;
        this.touch.startX = e.touches[0].clientX;
        this.touch.startY = e.touches[0].clientY;
        this.touch.lastX  = e.touches[0].clientX;
        this.touch.lastY  = e.touches[0].clientY;
      }
    }

    onTouchMove(e) {
      // ── Two-finger pinch: focal-point zoom + midpoint pan ──
      if (this.touch.isZooming && e.touches.length === 2) {
        e.preventDefault();

        const currentDist = this.getTouchDist(e.touches);
        const currentMidX = (e.touches[0].clientX + e.touches[1].clientX) / 2;
        const currentMidY = (e.touches[0].clientY + e.touches[1].clientY) / 2;

        // Incremental scale factor this frame
        const scaleFactor = this.touch.prevDist > 0 ? currentDist / this.touch.prevDist : 1;
        const rawScale    = this.transform.scale * scaleFactor;
        const newScale    = Math.min(4, Math.max(1, rawScale));
        // Actual factor after clamping (prevents drift past limits)
        const actualFactor = this.transform.scale > 0 ? newScale / this.transform.scale : scaleFactor;

        // Anchor = previous midpoint on the image's current visual surface.
        // dxFromLeft uses getBoundingClientRect().left — the VISUAL (post-transform) left edge.
        const imgRect = this.el.imgWrap.getBoundingClientRect();
        const anchorX = this.touch.prevMidX - imgRect.left;
        const anchorY = this.touch.prevMidY - imgRect.top;

        // Midpoint translation this frame (pinch + pan simultaneously)
        const midDx = currentMidX - this.touch.prevMidX;
        const midDy = currentMidY - this.touch.prevMidY;

        // Zoom-to-point: keep anchor at same viewport position, then add mid movement.
        // Derivation: newTx = anchorX*(1 - actualFactor) + currentTx + midDx
        let newTx = anchorX * (1 - actualFactor) + this.transform.tx + midDx;
        let newTy = anchorY * (1 - actualFactor) + this.transform.ty + midDy;

        const clamped = this.clampTransform(newTx, newTy, newScale);
        this.transform = { tx: clamped.tx, ty: clamped.ty, scale: newScale };
        this.applyTransform();

        this.touch.prevDist = currentDist;
        this.touch.prevMidX = currentMidX;
        this.touch.prevMidY = currentMidY;
        return;
      }

      // ── Single-finger: pan (zoomed) or swipe (at fit scale) ──
      if (this.touch.isDragging && e.touches.length === 1) {
        const currentX = e.touches[0].clientX;
        const currentY = e.touches[0].clientY;

        if (this.transform.scale > 1) {
          // Pan the zoomed image; prevent page scroll on both axes
          e.preventDefault();
          const dx = currentX - this.touch.lastX;
          const dy = currentY - this.touch.lastY;
          const clamped = this.clampTransform(
            this.transform.tx + dx,
            this.transform.ty + dy,
            this.transform.scale
          );
          this.transform = { ...this.transform, tx: clamped.tx, ty: clamped.ty };
          this.applyTransform();
        } else {
          // Swipe to navigate; only prevent scroll if clearly horizontal
          const dx = currentX - this.touch.startX;
          const dy = currentY - this.touch.startY;
          if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 8) {
            e.preventDefault();
          }
        }

        this.touch.lastX = currentX;
        this.touch.lastY = currentY;
      }
    }

    onTouchEnd(e) {
      // One finger remains after lifting during a pinch — end zoom mode cleanly
      if (e.touches.length === 1 && this.touch.isZooming) {
        this.touch.isZooming = false;
        this.touch.isDragging = false;
        if (this.transform.scale < 1) this.resetTransform();
        return;
      }

      if (e.touches.length > 0) return;

      // All fingers lifted after pinch
      if (this.touch.isZooming) {
        this.touch.isZooming = false;
        if (this.transform.scale < 1) this.resetTransform();
        return;
      }

      if (!this.touch.isDragging) return;
      this.touch.isDragging = false;

      const dx = this.touch.lastX - this.touch.startX;
      const dy = this.touch.lastY - this.touch.startY;
      const isTap = Math.abs(dx) <= 10 && Math.abs(dy) <= 10;

      if (isTap) {
        const isDouble = this.recordTap(this.touch.lastX, this.touch.lastY);
        if (isDouble) {
          this.handleDoubleTap(this.touch.lastX, this.touch.lastY);
          return;
        }
      }

      // Swipe navigation — only when at fit scale and it was a clear horizontal swipe
      if (!isTap && this.transform.scale <= 1) {
        if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy)) {
          this.goTo(this.currentIndex + (dx < 0 ? 1 : -1));
        }
      }
    }

    // Returns true if this tap completes a double-tap sequence
    recordTap(x, y) {
      const now = Date.now();
      if (
        this.lastTapTime &&
        now - this.lastTapTime < 300 &&
        Math.hypot(x - this.lastTapX, y - this.lastTapY) < 40
      ) {
        this.lastTapTime = 0; // reset so triple-tap doesn't chain
        return true;
      }
      this.lastTapTime = now;
      this.lastTapX = x;
      this.lastTapY = y;
      return false;
    }

    // Double-tap: zoom 2.5x toward the tapped point, or reset to fit if already zoomed.
    handleDoubleTap(tapX, tapY) {
      const animated = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      if (this.transform.scale > 1) {
        // Reset to fit
        if (animated) this.el.imgWrap.style.transition = 'transform 0.28s ease-out';
        this.resetTransform();
        if (animated) setTimeout(() => { this.el.imgWrap.style.transition = ''; }, 300);
      } else {
        // Zoom 2.5x toward the tap point.
        // With scale=1, tx=0, ty=0 and transform-origin: 0 0:
        //   newTx = dxFromLeft * (1 - 2.5) = -1.5 * dxFromLeft
        const imgRect  = this.el.imgWrap.getBoundingClientRect();
        const newScale = 2.5;
        const dxFromLeft = tapX - imgRect.left;
        const dyFromTop  = tapY - imgRect.top;
        const clamped = this.clampTransform(
          dxFromLeft * (1 - newScale),
          dyFromTop  * (1 - newScale),
          newScale
        );
        if (animated) this.el.imgWrap.style.transition = 'transform 0.28s ease-out';
        this.transform = { tx: clamped.tx, ty: clamped.ty, scale: newScale };
        this.applyTransform();
        if (animated) setTimeout(() => { this.el.imgWrap.style.transition = ''; }, 300);
      }
    }

    getTouchDist(touches) {
      return Math.hypot(
        touches[0].clientX - touches[1].clientX,
        touches[0].clientY - touches[1].clientY
      );
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
